/**
 * `/tasks` — guided task catalog (M3 Phase A).
 *
 * Renders a filterable, Apex-Fusion-style table of every entry in
 * `TASK_CATALOG`. Operators search by Title / Description / Tag,
 * click a row, and the runner modal opens to walk them through.
 *
 * Filter state lives only in component state — we don't put it in the
 * URL because the table is short enough that scrolling back to a row
 * is faster than deep-linking a filter combo.
 *
 * The table is fully keyboard-navigable: rows are real `<button>`s and
 * the "Run" affordance is the row itself so there's no double-tab
 * dance between the row label and the action.
 */
import { useMemo, useState } from 'react';

import { TASK_CATALOG } from '../tasks/catalog';
import { TaskRunnerModal } from '../tasks/TaskRunnerModal';
import type { TaskDefinition } from '../tasks/types';

// ─────────────────────────────────────────────────────────────────────
// Filter helpers
// ─────────────────────────────────────────────────────────────────────

function normaliseSearch(s: string): string {
  return s.trim().toLowerCase();
}

function matchesText(task: TaskDefinition, q: string): boolean {
  if (q.length === 0) return true;
  if (task.title.toLowerCase().includes(q)) return true;
  if (task.description.toLowerCase().includes(q)) return true;
  if (task.tags.some((t) => t.toLowerCase().includes(q))) return true;
  return false;
}

function matchesTag(task: TaskDefinition, tag: string): boolean {
  if (tag === '') return true;
  return task.tags.includes(tag);
}

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────

export function TasksPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [activeTask, setActiveTask] = useState<TaskDefinition | null>(null);

  /** Sorted, deduped union of every task's tags. */
  const allTags = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const t of TASK_CATALOG) for (const tag of t.tags) set.add(tag);
    return [...set].sort();
  }, []);

  const filtered = useMemo<TaskDefinition[]>(() => {
    const q = normaliseSearch(search);
    return TASK_CATALOG.filter(
      (t) => matchesText(t, q) && matchesTag(t, tagFilter),
    );
  }, [search, tagFilter]);

  return (
    <div className="p-4 sm:p-6" data-testid="tasks-page">
      {/* Page header */}
      <header className="mb-4 flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-reef-100">Tasks</h1>
        <p className="text-sm text-reef-400">
          Guided flows for the things you actually want to do — add a
          device, calibrate a pump, set up a broker. Pick one and walk
          through the steps.
        </p>
      </header>

      {/* Filter bar */}
      <div
        className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center"
        data-testid="tasks-filter-bar"
      >
        <input
          type="search"
          placeholder="Search title / description / tag…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="tasks-search"
          className="w-full sm:max-w-md rounded-md border border-reef-700 bg-reef-950 px-3 py-1.5 text-sm text-reef-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <select
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          data-testid="tasks-tag-filter"
          className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <option value="">All tags</option>
          {allTags.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <span
          className="text-xs text-reef-500"
          data-testid="tasks-count"
          aria-live="polite"
        >
          Showing {filtered.length} of {TASK_CATALOG.length}
        </span>
      </div>

      {/* Table */}
      <div
        className="overflow-x-auto rounded-lg border border-reef-800 bg-reef-950/40"
        data-testid="tasks-table-wrap"
      >
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-reef-500">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold">
                Title
              </th>
              <th scope="col" className="px-3 py-2 font-semibold">
                Description
              </th>
              <th scope="col" className="px-3 py-2 font-semibold">
                Tags
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-reef-800">
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-6 text-center text-sm text-reef-500"
                  data-testid="tasks-empty"
                >
                  No tasks match your filters.
                </td>
              </tr>
            ) : (
              filtered.map((task) => {
                const note = task.requires?.note;
                return (
                  <tr
                    key={task.id}
                    data-testid={`tasks-row-${task.id}`}
                    className="hover:bg-reef-900/50"
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-reef-100">
                        {task.title}
                      </div>
                      {note ? (
                        <div className="mt-0.5 text-[11px] text-reef-500">
                          {note}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 align-top text-reef-300">
                      {task.description}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-1">
                        {task.tags.map((t) => (
                          <span
                            key={t}
                            className="rounded-full bg-reef-800 px-2 py-0.5 text-[10px] text-reef-300"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top text-right">
                      <button
                        type="button"
                        onClick={() => setActiveTask(task)}
                        data-testid={`tasks-run-${task.id}`}
                        className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-medium text-reef-950 hover:bg-amber-400"
                      >
                        Run
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Runner */}
      {activeTask ? (
        <TaskRunnerModal
          open={true}
          task={activeTask}
          onClose={() => setActiveTask(null)}
        />
      ) : null}
    </div>
  );
}